import { useCallback, useEffect, useState } from 'react';
import { endpoint, env, ForbiddenResponseError, getAuthToken, handleUnauthorized, UnknownResponseError, } from '@eruvaka/web-pondlogs-base';
const isRecord = (value) => typeof value === 'object' && value !== null;
const toLocationOption = (value) => {
    if (!isRecord(value))
        return null;
    const id = typeof value.locationId === 'string' && value.locationId
        ? value.locationId
        : typeof value.id === 'string' && value.id
            ? value.id
            : typeof value._id === 'string' && value._id
                ? value._id
                : '';
    const name = typeof value.name === 'string' ? value.name.trim() : '';
    if (!id || !name)
        return null;
    return {
        id,
        name,
        city: typeof value.city === 'string' && value.city.trim() ? value.city.trim() : null,
        state: typeof value.state === 'string' && value.state.trim() ? value.state.trim() : null,
    };
};
const extractLocations = (payload) => {
    const arr = Array.isArray(payload)
        ? payload
        : isRecord(payload) && Array.isArray(payload.data)
            ? payload.data
            : isRecord(payload) &&
                isRecord(payload.data) &&
                Array.isArray(payload.data.locations)
                ? payload.data.locations
                : [];
    return arr.map(toLocationOption).filter((l) => l !== null);
};
const LOCATIONS_CACHE_TTL = 30_000; // 30 seconds
let _locationsCache = null;
let _inflightLocations = null;
export async function fetchLocations() {
    if (_locationsCache && Date.now() - _locationsCache.ts < LOCATIONS_CACHE_TTL) {
        return _locationsCache.data;
    }
    if (_inflightLocations)
        return _inflightLocations;
    _inflightLocations = (async () => {
        try {
            const token = getAuthToken();
            const url = endpoint(env.apiBaseUrl, '/api/v3/locations');
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            };
            const response = await fetch(url, { method: 'GET', credentials: 'include', headers });
            if (!response.ok) {
                const body = await response.text();
                if (response.status === 401) {
                    handleUnauthorized();
                    throw new UnknownResponseError(response.status, body);
                }
                if (response.status === 403)
                    throw new ForbiddenResponseError(body);
                throw new UnknownResponseError(response.status, body);
            }
            let result;
            try {
                result = extractLocations(await response.json());
            }
            catch {
                result = [];
            }
            _locationsCache = { data: result, ts: Date.now() };
            return result;
        }
        finally {
            _inflightLocations = null;
        }
    })();
    return _inflightLocations;
}
export const useLocations = () => {
    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchLocations();
            setLocations(data);
        }
        catch (err) {
            setError('errors.loadFailed');
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    return { locations, isLoading, error, refetch: load };
};
export const createLocation = async (input) => {
    const token = getAuthToken();
    const url = endpoint(env.apiBaseUrl, '/api/v3/locations');
    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(input),
    });
    if (!response.ok) {
        const body = await response.text();
        if (response.status === 401) {
            handleUnauthorized();
            throw new UnknownResponseError(response.status, body);
        }
        if (response.status === 403)
            throw new ForbiddenResponseError(body);
        throw new UnknownResponseError(response.status, body);
    }
    const json = await response.json();
    const raw = isRecord(json) && isRecord(json.data) ? json.data : json;
    // Invalidate the locations cache so the next fetch picks up the new location
    _locationsCache = null;
    return (toLocationOption(raw) ?? {
        id: '',
        name: input.name,
        city: input.city ?? null,
        state: input.state ?? null,
    });
};
